if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      this.stickyButton = this.querySelector(`#StickySubmitButton-${this.sectionId}`);
      this.stickyBuyNowButton = this.querySelector(`#StickyBuyNowButton-${this.sectionId}`);
      this.mainPrice = document.getElementById(`price-${this.sectionId}`);
      this.stickyPrice = this.querySelector(`#sticky-price-${this.sectionId}`);
      this.buttonWrapper = this.mainButton ? this.mainButton.closest('.product-form__buttons') : null;
    }

    connectedCallback() {
      if (!this.mainButton || !this.stickyButton) return;

      this.initIntersectionObserver();
      this.initButtonObserver();
      this.initPriceObserver();
      this.initClickListener();
      this.initDynamicCheckoutObserver();
      this.initBuyNowClickListener();
    }

    disconnectedCallback() {
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.buttonObserver) this.buttonObserver.disconnect();
      if (this.priceObserver) this.priceObserver.disconnect();
      if (this.buyNowObserver) this.buyNowObserver.disconnect();
      if (this.dynamicCheckoutContainerObserver) this.dynamicCheckoutContainerObserver.disconnect();
    }

    initIntersectionObserver() {
      const target = this.buttonWrapper || this.mainButton;
      
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          // Show the sticky bar only when we scroll down past the original button
          if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
            this.classList.add('is-visible');
          } else {
            this.classList.remove('is-visible');
          }
        });
      }, { threshold: 0 });

      this.intersectionObserver.observe(target);
    }

    initButtonObserver() {
      this.buttonObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
            if (this.mainButton.disabled) {
              this.stickyButton.setAttribute('disabled', 'disabled');
            } else {
              this.stickyButton.removeAttribute('disabled');
            }
          }
        });
        // Always sync the text content
        const mainSpan = this.mainButton.querySelector('span');
        const stickySpan = this.stickyButton.querySelector('span');
        if (mainSpan && stickySpan) {
          stickySpan.innerHTML = mainSpan.innerHTML;
        }
      });

      this.buttonObserver.observe(this.mainButton, { attributes: true, childList: true, subtree: true });
    }

    initPriceObserver() {
      if (!this.mainPrice || !this.stickyPrice) return;

      this.priceObserver = new MutationObserver(() => {
        this.stickyPrice.innerHTML = this.mainPrice.innerHTML;
      });

      this.priceObserver.observe(this.mainPrice, { childList: true, subtree: true });
    }

    initClickListener() {
      this.stickyButton.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Show loading spinner on sticky button
        this.stickyButton.classList.add('loading');
        const spinner = this.stickyButton.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.remove('hidden');

        // Trigger the main button click
        this.mainButton.click();
        
        // Remove loading state after a short delay (Shopify's cart JS will handle the rest)
        setTimeout(() => {
          this.stickyButton.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }, 1000);
      });
    }

    initDynamicCheckoutObserver() {
      if (!this.buttonWrapper || !this.stickyBuyNowButton) return;

      const checkForDynamicCheckout = () => {
        // Look for either the container or the button itself
        const paymentContainer = this.buttonWrapper.querySelector('.shopify-payment-button');
        const standardBuyNow = this.buttonWrapper.querySelector('.shopify-payment-button__button, .shopify-payment-button [role="button"]');
        const oneCheckoutBtn = document.getElementById('buyNow1CheckoutBtn');
        
        this.mainBuyNowButton = oneCheckoutBtn || standardBuyNow;
        
        // If either exists, we assume dynamic checkout is enabled for this product
        if (paymentContainer || this.mainBuyNowButton) {
          this.stickyBuyNowButton.classList.remove('hidden');

          if (this.mainBuyNowButton && (!this.buyNowObserver || this.buyNowObserverTarget !== this.mainBuyNowButton)) {
            if (this.buyNowObserver) this.buyNowObserver.disconnect();
            
            this.buyNowObserverTarget = this.mainBuyNowButton;
            this.buyNowObserver = new MutationObserver((mutations) => {
              mutations.forEach((mutation) => {
                if (mutation.type === 'attributes') {
                   const isDisabled = this.mainBuyNowButton.disabled || 
                                      this.mainBuyNowButton.getAttribute('disabled') === 'disabled' || 
                                      this.mainBuyNowButton.getAttribute('aria-disabled') === 'true';
                   
                   if (isDisabled) {
                     this.stickyBuyNowButton.setAttribute('disabled', 'disabled');
                   } else {
                     this.stickyBuyNowButton.removeAttribute('disabled');
                   }
                }
              });
            });
            this.buyNowObserver.observe(this.mainBuyNowButton, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
          }
        } else {
          this.stickyBuyNowButton.classList.add('hidden');
        }
      };

      checkForDynamicCheckout();

      this.dynamicCheckoutContainerObserver = new MutationObserver(() => {
        checkForDynamicCheckout();
      });

      const observeTarget = this.buttonWrapper || this.mainButton.closest('form') || document.body;
      this.dynamicCheckoutContainerObserver.observe(observeTarget, { childList: true, subtree: true });
    }

    initBuyNowClickListener() {
      if (!this.stickyBuyNowButton) return;
      
      this.stickyBuyNowButton.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Fetch dynamically in case it loaded late
        const oneCheckoutBtn = document.getElementById('buyNow1CheckoutBtn');
        const targetBtn = oneCheckoutBtn || (this.buttonWrapper ? this.buttonWrapper.querySelector('.shopify-payment-button__button, .shopify-payment-button [role="button"]') : null);
        
        if (targetBtn) {
          this.stickyBuyNowButton.classList.add('loading');
          const spinner = this.stickyBuyNowButton.querySelector('.loading-overlay__spinner');
          if (spinner) spinner.classList.remove('hidden');

          targetBtn.click();

          setTimeout(() => {
            this.stickyBuyNowButton.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
          }, 1000);
        }
      });
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
